import { useReactiveVar } from '@apollo/client';
import {
  ArrowBack,
  Campaign,
  KeyboardArrowDown,
  SendOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Popover,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { Body, Heading } from '@/components/Typography';
import { useListAgencySkillsQuery } from '@/graphql';
import useMediaQuery from '@/hooks/useMediaQuery';
import styled from '@/styles';
import breakpoints from '@/styles/breakpoints';
import colors, { IconColor, theme } from '@/styles/colors';
import { currentAgencyVar } from '@/util/apollo/cache';
import CheckboxList from '@/routes/Agency/Chat/Pubnub/SendBulkMessage/CheckboxList';
import SelectSkillsModal from '@/routes/Agency/Chat/Pubnub/SendBulkMessage/SelectSkillsModal';
import { isValidUSZip } from '@/routes/Agency/Chat/Pubnub/utils';

import { useCreateBroadcast } from '../api/useCreateBroadcast';
import { buildFilteredAudience } from '../lib/audienceFilter';

interface Skill {
  id: string;
  name: string;
}

interface CommsBroadcastComposerProps {
  onCreated: (broadcastId: string) => void;
  onCancel: () => void;
}

const Wrapper = styled('div', {
  maxHeight: '91vh',
  overflow: 'auto',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});
const Section = styled('div', {
  backgroundColor: 'white',
  width: '100%',
  padding: '14px 22px',
  height: '64px',
  '@phoneOnly': {
    padding: '10px 12px',
    height: '54px',
  },
});

const checkboxStyles = {
  '&.Mui-checked': {
    color: theme.themeDefault,
  },
};
const InputStyles = {
  marginLeft: '10px',
  verticalAlign: 'middle',
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'white' },
    '&:hover fieldset': { borderColor: 'white' },
    '&.Mui-focused fieldset': { borderColor: colors.themeLight },
  },
};

const MAX_CONTENT_LENGTH = 500;

export function CommsBroadcastComposer({
  onCreated,
  onCancel,
}: CommsBroadcastComposerProps) {
  const phoneOnly = useMediaQuery(breakpoints.phoneOnly);
  const currentAgency = useReactiveVar(currentAgencyVar);
  const { data } = useListAgencySkillsQuery({
    variables: { agencyId: currentAgency!.id },
  });

  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]);
  const [selectedTaxTypes, setSelectedTaxTypes] = useState<
    Array<'w2' | '1099'>
  >(['w2', '1099']);
  const [miles, setMiles] = useState<number | undefined>();
  const [zipCode, setZipCode] = useState('');
  const [showskillsModal, setShowSkillsModal] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { create, creating } = useCreateBroadcast();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (data?.agency) {
      const items = data.agency.skillCategories
        .flatMap((category) => category.skills)
        .map((s) => ({ id: s.id, name: s.name }));
      setAllSkills(items);
      setSelectedSkills(items);
    }
  }, [data]);

  const allSelected =
    allSkills.length > 0 && allSkills.length === selectedSkills.length;

  const skillsNames = useMemo(() => {
    return selectedSkills
      .slice(0, phoneOnly ? 2 : 6)
      .map((s) => s.name)
      .join(', ');
  }, [selectedSkills, phoneOnly]);

  const overflowCount = phoneOnly
    ? selectedSkills.length - 2
    : selectedSkills.length - 6;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (phoneOnly) {
      setShowSkillsModal(true);
      return;
    }
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleTaxTypeCheck = (checked: boolean, type: 'w2' | '1099') => {
    if (checked) {
      setSelectedTaxTypes(Array.from(new Set([...selectedTaxTypes, type])));
    } else {
      setSelectedTaxTypes(selectedTaxTypes.filter((t) => t !== type));
    }
  };

  const validate = (): string | null => {
    if (!message.trim()) return 'Broadcast message is required.';
    if (message.length > MAX_CONTENT_LENGTH) {
      return `Broadcast must be 1–${MAX_CONTENT_LENGTH} characters.`;
    }
    if (allSkills.length > 0 && selectedSkills.length === 0) {
      return 'At least one skill needs to be selected.';
    }
    if (selectedTaxTypes.length === 0) {
      return 'At least one tax type needs to be selected.';
    }
    if (zipCode && !isValidUSZip(zipCode)) {
      return 'Not a valid zip code.';
    }
    if (zipCode && !miles) return 'Miles is required for zip code.';
    if (!zipCode && miles) return 'Zip code is required with miles.';
    if (miles !== undefined) {
      if (miles < 2) return 'The distance has to be 2mi at least.';
      if (miles > 100)
        return 'The given miles are exceeding the maximum distance limit of 100mi.';
    }
    return null;
  };

  const handleSend = async () => {
    const errMsg = validate();
    if (errMsg) {
      setError(errMsg);
      return;
    }
    setError('');
    const audience = buildFilteredAudience({
      skillIds: selectedSkills.map((s) => Number(s.id)),
      taxTypes: selectedTaxTypes,
      zipCode: zipCode || undefined,
      radiusMiles: miles,
    });
    try {
      const result = await create({
        content: message.trim(),
        audienceFilter: audience,
      });
      onCreated(result.broadcast_id);
    } catch (e: unknown) {
      const apiMsg =
        (e as { response?: { data?: { errors?: string[] } } })?.response?.data
          ?.errors?.[0] ?? (e as Error)?.message;
      setError(apiMsg ?? 'Failed to send broadcast.');
    }
  };

  return (
    <Wrapper>
      <div>
        <Section>
          <Stack alignItems="center" direction="row" spacing={2}>
            {phoneOnly ? (
              <ArrowBack onClick={onCancel} />
            ) : (
              <IconButton
                style={{
                  backgroundColor: theme.themeDefault,
                  borderRadius: '24px',
                  cursor: 'alias',
                }}
              >
                <Campaign htmlColor="white" />
              </IconButton>
            )}
            <Heading>New Broadcast</Heading>
          </Stack>
        </Section>
        <Divider />
        <Section>
          <Grid container alignItems="center" spacing={{ xs: 0, sm: 0.5 }}>
            <Grid item sm={1.5} xs={3.2}>
              <Body size={phoneOnly ? 'md' : 'lg'}>Choose Skills:</Body>
            </Grid>
            <Grid item sm={10} xs={8}>
              <Body size={phoneOnly ? 'md' : 'lg'}>
                <b>{allSelected ? 'All' : skillsNames}</b>
                {overflowCount > 0 && !allSelected ? ` +${overflowCount}` : ''}
              </Body>
            </Grid>
            <Grid item sm={0.5} xs={0.8}>
              <IconButton
                id="show-skills-btn"
                style={{ paddingLeft: phoneOnly ? '0px' : undefined }}
                onClick={handleClick}
              >
                <KeyboardArrowDown htmlColor={IconColor} />
              </IconButton>
            </Grid>
          </Grid>
        </Section>
        <Divider />
        <Section>
          <Stack alignItems="center" direction="row" spacing={2}>
            <Body size={phoneOnly ? 'md' : 'lg'}>Tax Type:</Body>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTaxTypes.includes('w2')}
                  id="tax-type-w2"
                  sx={checkboxStyles}
                  onChange={(_, checked) => handleTaxTypeCheck(checked, 'w2')}
                />
              }
              label="W2"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTaxTypes.includes('1099')}
                  id="tax-type-1099"
                  sx={checkboxStyles}
                  onChange={(_, checked) => handleTaxTypeCheck(checked, '1099')}
                />
              }
              label="1099"
            />
          </Stack>
        </Section>
        <Divider />
        <Section>
          <Stack direction="row" spacing={{ xs: 2, sm: 6 }}>
            <div>
              <Body as="span" size={phoneOnly ? 'md' : 'lg'}>
                Within:
              </Body>
              <TextField
                id="miles-input"
                inputProps={{ style: { padding: '6px' } }}
                placeholder="Miles"
                size="small"
                sx={{ ...InputStyles, width: '75px' }}
                type="number"
                variant="outlined"
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) setMiles(Number(val));
                  else setMiles(undefined);
                }}
              />
            </div>
            <div>
              <Body as="span" size={phoneOnly ? 'md' : 'lg'}>
                Miles of:
              </Body>
              <TextField
                id="zipcode-input"
                inputProps={{ style: { padding: '6px' } }}
                placeholder="Zipcode"
                size="small"
                sx={{ ...InputStyles, width: '90px' }}
                variant="outlined"
                onChange={(e) => setZipCode(e.target.value)}
              />
            </div>
          </Stack>
        </Section>

        <Popover
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          open={open}
          slotProps={{
            paper: { style: { width: '400px', maxHeight: '70vh' } },
          }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={handleClose}
        >
          <CheckboxList
            allSelected={allSelected}
            data={allSkills}
            selectedData={selectedSkills}
            setSelectedOptions={setSelectedSkills}
          />
        </Popover>

        {showskillsModal && (
          <SelectSkillsModal
            data={allSkills}
            hideModal={() => setShowSkillsModal(false)}
            selectedData={selectedSkills}
            setSelectedOptions={setSelectedSkills}
            title="Select Skills"
          />
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {creating && <LinearProgress />}
      </div>

      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            backgroundColor: 'white',
            borderRadius: phoneOnly ? 12 : 24,
            padding: phoneOnly ? '6px 8px 6px 14px' : '8px 10px 8px 18px',
            margin: phoneOnly ? '0 10px 6px' : '0 16px 6px',
          }}
        >
          <TextField
            multiline
            inputProps={{ maxLength: MAX_CONTENT_LENGTH }}
            maxRows={phoneOnly ? 4 : 5}
            minRows={phoneOnly ? 1 : 2}
            placeholder="Type a broadcast message"
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                padding: 0,
                '& fieldset': { display: 'none' },
              },
              '& .MuiOutlinedInput-input': {
                padding: 0,
              },
            }}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <IconButton
            disabled={!message.trim() || creating}
            id="send-broadcast-btn"
            sx={{
              alignSelf: 'flex-end',
              flexShrink: 0,
              width: 36,
              height: 36,
              backgroundColor: theme.themeDefault,
              '&:hover': { backgroundColor: theme.themeDefault },
              '&.Mui-disabled': {
                backgroundColor: theme.themeDefault,
                opacity: 0.5,
              },
            }}
            onClick={handleSend}
          >
            <SendOutlined fontSize="small" htmlColor="white" />
          </IconButton>
        </div>
        <div
          style={{
            textAlign: 'right',
            fontSize: 11,
            color: '#6b7280',
            paddingRight: phoneOnly ? 16 : 22,
            marginBottom: phoneOnly ? 10 : 16,
          }}
        >
          {message.length}/{MAX_CONTENT_LENGTH}
        </div>
      </div>
    </Wrapper>
  );
}
